import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../layout/MainLayout";
import Swal from "sweetalert2";
import { listMyAssignedKawasans, listTunggakan } from "../lib/api";

function formatMoney(v) {
  const n = Number(v || 0);
  return n.toFixed(2);
}

export default function TunggakanPage() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const [kawasanList, setKawasanList] = useState([]);
  const [kawasan, setKawasan] = useState("");

  const [search, setSearch] = useState("");

  const loadKawasans = async () => {
    try {
      const list = await listMyAssignedKawasans();
      const arr = Array.isArray(list) ? list : [];

      const normalized = arr
        .map((x) => {
          const kawasanName = x?.kawasan || x?.kawasan_name || "";
          const kodKawasan = x?.kod_kawasan || x?.kodKawasan || "";
          return {
            key: kodKawasan || kawasanName,
            kawasan: kawasanName,
            kodKawasan,
            zon: x?.zon || "",
          };
        })
        .filter((x) => x.kawasan);

      const uniq = [];
      const seen = new Set();

      for (const item of normalized) {
        if (seen.has(item.key)) continue;
        seen.add(item.key);
        uniq.push(item);
      }

      uniq.sort((a, b) => a.kawasan.localeCompare(b.kawasan));
      setKawasanList(uniq);
    } catch (e) {
      console.error("loadKawasans error:", e);
      await Swal.fire({
        icon: "error",
        title: "Gagal",
        text: e?.response?.data?.error || "Gagal load kawasan.",
      });
    }
  };

  const load = async () => {
    try {
      setLoading(true);

      const params = {};
      if (kawasan) {
        const selectedArea = kawasanList.find((item) => item.key === kawasan);
        if (selectedArea?.kodKawasan) params.kod_kawasan = selectedArea.kodKawasan;
        else params.kawasan = selectedArea?.kawasan || kawasan;
      }

      const data = await listTunggakan(params);
      const items = Array.isArray(data?.items) ? data.items : [];
      setRows(items);
    } catch (e) {
      console.error("loadTunggakan error:", e);
      await Swal.fire({
        icon: "error",
        title: "Gagal",
        text: e?.response?.data?.error || "Gagal load tunggakan.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKawasans();
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kawasan]);

  const filteredRows = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();

    let list = rows.map((r) => {
      const noAkaun = r.no_akaun || r.noAkaun || "-";
      const kawasanName = r.kawasan || "-";
      const telefon = r.telefon || "-";
      const bil = r.bil || "-";
      const nomborUnit = r.nombor_unit || r.nomborUnit || "-";
      const tunggakan = Number(
        r.tunggakan ?? r.arrears_amount ?? r.tunggakan_sewa ?? 0
      );

      return {
        ...r,
        noAkaun,
        kawasan: kawasanName,
        telefon,
        bil,
        nomborUnit,
        tunggakan,
      };
    });

    if (q) {
      list = list.filter((r) => {
        const haystack = [
          r.noAkaun,
          r.kawasan,
          r.telefon,
          r.bil,
          r.nomborUnit,
        ]
          .join(" ")
          .toLowerCase();

        return haystack.includes(q);
      });
    }

    list.sort((a, b) => b.tunggakan - a.tunggakan);

    return list;
  }, [rows, search]);

  const summary = useMemo(() => {
    const totalCount = filteredRows.length;
    const totalAmount = filteredRows.reduce(
      (sum, r) => sum + Number(r.tunggakan || 0),
      0
    );
    const kawasanCount = new Set(
      filteredRows.map((x) => x.kawasan).filter(Boolean)
    ).size;

    return {
      totalCount,
      totalAmount: totalAmount.toFixed(2),
      kawasanCount,
    };
  }, [filteredRows]);

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto bg-white shadow-md rounded-lg p-6 mt-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Tunggakan</h2>
            <p className="text-xs text-gray-500 mt-1">
              Papar akaun yang mempunyai baki tunggakan semasa.
            </p>
          </div>

          <button
            onClick={load}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs text-gray-600">Kawasan</label>
            <select
              className="border rounded-md w-full p-2 text-sm bg-white"
              value={kawasan}
              onChange={(e) => setKawasan(e.target.value)}
            >
              <option value="">Semua Kawasan</option>
              {kawasanList.map((k) => (
                <option key={k.key} value={k.key}>
                  {k.kodKawasan ? `${k.kodKawasan} - ` : ""}
                  {k.kawasan}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-3">
            <label className="text-xs text-gray-600">Search</label>
            <input
              type="text"
              className="border rounded-md w-full p-2 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="No Akaun / Kawasan / Telefon / BIL / Unit"
            />
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
          <div className="p-3 rounded border bg-red-50">
            <div className="text-xs text-gray-600">JUMLAH TUNGGAKAN</div>
            <div className="font-semibold">{filteredRows.length} rekod</div>
            <div className="font-bold text-red-700">RM {summary.totalAmount}</div>
          </div>

          <div className="p-3 rounded border bg-orange-50">
            <div className="text-xs text-gray-600">AKAUN TERTUNGGAK</div>
            <div className="font-semibold">{summary.totalCount} rekod</div>
            <div className="font-bold text-orange-700">Perlu tindakan</div>
          </div>

          <div className="p-3 rounded border bg-blue-50">
            <div className="text-xs text-gray-600">KAWASAN</div>
            <div className="font-semibold">{summary.kawasanCount}</div>
            <div className="font-bold text-blue-700">Dalam paparan</div>
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm border">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-2 border">No Akaun</th>
                <th className="text-left p-2 border">Kawasan</th>
                <th className="text-left p-2 border">Telefon</th>
                <th className="text-left p-2 border">Tunggakan (RM)</th>
                <th className="text-left p-2 border">Status</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="p-3 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-3 text-center text-gray-500">
                    Tiada data.
                  </td>
                </tr>
              ) : (
                filteredRows.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-gray-50 bg-red-50">
                    <td className="p-2 border font-semibold text-gray-800">
                      {r.noAkaun}
                      {r.bil && (
                        <div className="text-xs text-gray-500">BIL: {r.bil}</div>
                      )}
                      {r.nomborUnit && r.nomborUnit !== "-" && (
                        <div className="text-xs text-gray-500">Unit: {r.nomborUnit}</div>
                      )}
                    </td>

                    <td className="p-2 border">{r.kawasan}</td>
                    <td className="p-2 border">{r.telefon}</td>
                    <td className="p-2 border">{formatMoney(r.tunggakan)}</td>
                    <td className="p-2 border">
                      <span className="text-xs font-bold px-2 py-1 rounded bg-red-100 text-red-700">
                        TUNGGAKAN
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-3 text-xs text-gray-500">
          Nota: Data tunggakan ialah snapshot semasa; rekod dipaparkan apabila tunggakan melebihi RM 0.
        </div>
      </div>
    </MainLayout>
  );
}
